import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { PaymentOverlay } from './PaymentOverlay';
import { usePaymentOverlay } from './usePaymentOverlay';
import type { PaymentAddMode, PaymentMountConfig } from './types';
import { registerDynamicBootstrap } from '../../shared/registerDynamicBootstrap';
import './payment.css';

const mountSelector = '#ips-react-payment-root';

const normalizeMode = (value: string | undefined): PaymentAddMode =>
  value === 'page' ? 'page' : 'modal';

const normalizeEndpoint = (value: string | undefined): string => (value || '').replace(/^\/+|\/+$/g, '');

const bootstrap = () => {
  const mount = document.querySelector<HTMLElement>(mountSelector);
  if (!mount || mount.dataset.ipsReactMounted === '1') {
    return;
  }

  const addMode = normalizeMode(mount.dataset.addMode);
  const addUrl = mount.dataset.addUrl || '';
  const openPageUrl = mount.dataset.openPageUrl || addUrl;
  const listUrl = mount.dataset.listUrl || '';
  const openQueryKey = mount.dataset.openQueryKey || 'ips_add_payment';
  const addEndpoint = normalizeEndpoint(mount.dataset.addEndpoint);
  const listEndpoint = normalizeEndpoint(mount.dataset.listEndpoint);
  const openFromData = mount.dataset.openAdd === '1';

  if (!addUrl) {
    return;
  }

  mount.dataset.ipsReactMounted = '1';

  const config: PaymentMountConfig = {
    addMode,
    addUrl,
    openPageUrl,
    listUrl,
    openQueryKey,
    addEndpoint,
    listEndpoint,
    openFromData,
  };

  const root = createRoot(mount);

  const App: React.FC = () => {
    const { open, loading, openModal, closeModal, syncFromHistory, handleIframeLoad } = usePaymentOverlay(config);

    useEffect(() => {
      if (config.addMode !== 'modal') {
        return;
      }

      const handler = (event: Event) => {
        const target = (event.target as HTMLElement)?.closest<HTMLElement>('[data-ips-payment-add-trigger]');
        if (!target) return;

        event.preventDefault();
        openModal();
      };

      document.addEventListener('click', handler, true);
      return () => document.removeEventListener('click', handler, true);
    }, [openModal]);

    useEffect(() => {
      if (config.addMode !== 'modal') {
        return;
      }

      window.addEventListener('popstate', syncFromHistory);
      return () => window.removeEventListener('popstate', syncFromHistory);
    }, [syncFromHistory]);

    if (config.addMode !== 'modal' || !open) {
      return null;
    }

    return (
      <PaymentOverlay
        open={open}
        loading={loading}
        addUrl={config.addUrl}
        openPageUrl={config.openPageUrl}
        listUrl={config.listUrl}
        onClose={() => closeModal()}
        onIframeLoad={handleIframeLoad}
      />
    );
  };

  root.render(<App />);
};

registerDynamicBootstrap(bootstrap);
